import {
  Box,
  IconButton,
  Link,
  ListItem,
  ListItemAvatar,
  Typography,
  alpha,
  makeStyles,
} from '@material-ui/core';
import InsertDriveFileIcon from '@material-ui/icons/InsertDriveFile';
import PlayArrowIcon from '@material-ui/icons/PlayArrow';
import ReactPlayer, { ReactPlayerProps } from 'react-player';

import { useSearchQuery } from 'contexts/SearchQueryContext';
import {
  FormatosDeCabecalhoMensagemTemplateEnum,
  WppButtonType,
  WppTemplateData,
} from 'store/reducers/templates';
import { getFileNameAndExtension } from 'utils/getFileNameAndExtension';
import parseTextToHtml from 'utils/parseTextToHtml';

import ImageViewer from './ImageViewer';
import TemplateButtons from './TemplateMessageButtons';
import TemplateMessagePix from './TemplateMessagePix';

const useStyles = makeStyles(theme => ({
  content: {
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing(2),
    height: '100%',
  },
  messageContainer: {
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing(0.5),
  },
  headerText: {
    fontSize: theme.typography.body2.fontSize,
    fontWeight: 600,
  },
  imageContainer: {
    minWidth: theme.spacing(40),
    height: theme.spacing(18),
    overflow: 'hidden',
    position: 'relative',
    borderRadius: theme.spacing(1),
  },
  document: {
    display: 'flex',
    alignItems: 'center',
    overflow: 'hidden',
    position: 'relative',
    minWidth: theme.spacing(40),
    paddingBottom: theme.spacing(1),
  },
  listItem: {
    color: 'inherit',
    borderRadius: theme.spacing(1),
  },
  titulo: {
    overflow: 'hidden',
    width: theme.spacing(24),
  },
  iconButton: {
    color: theme.palette.getContrastText(
      alpha(theme.palette.common.black, 0.4),
    ),
    backgroundColor: `${alpha(theme.palette.common.black, 0.4)} !important`,
  },
  bodyText: {
    fontSize: theme.typography.body2.fontSize,
  },
  footerText: {
    fontSize: theme.typography.caption.fontSize,
    color: theme.palette.type === 'light' ? '#c7c7c7' : '#bc9d9d',
    fontWeight: 500,
  },
  errorText: {
    color: theme.palette.error.main,
  },
}));

interface TemplateMessageContentProps {
  template: WppTemplateData | null;
  fromMe?: boolean;
  onShowMoreButtons?: (buttons: WppButtonType[]) => void;
}

export default function TemplateMessageContent({
  template,
  onShowMoreButtons,
  fromMe = false,
}: TemplateMessageContentProps) {
  const classes = useStyles();
  const searchQuery = useSearchQuery();
  const components = template?.components || [];

  const header = components.find(c => c.type === 'HEADER');
  const body = components.find(c => c.type === 'BODY');
  const footer = components.find(c => c.type === 'FOOTER');
  const buttons = components.find(c => c.type === 'BUTTONS');
  const hasContent = header || body || footer;

  const renderHeader = (format: FormatosDeCabecalhoMensagemTemplateEnum) => {
    switch (format) {
      case FormatosDeCabecalhoMensagemTemplateEnum.TEXTO:
        return (
          <Typography
            variant="subtitle2"
            className={classes.headerText}
            dangerouslySetInnerHTML={{
              __html: parseTextToHtml(header?.text || '', searchQuery),
            }}
          />
        );
      case FormatosDeCabecalhoMensagemTemplateEnum.IMAGEM:
        return (
          <Box className={classes.imageContainer}>
            <ImageViewer src={header?.example?.header_handle?.[0]}>
              <img
                src={header?.example?.header_handle?.[0]}
                alt=""
                style={{
                  width: '100%',
                  height: '100%',
                  objectFit: 'cover',
                  objectPosition: 'center',
                }}
              />
            </ImageViewer>
          </Box>
        );
      case FormatosDeCabecalhoMensagemTemplateEnum.DOCUMENTO: {
        if (!header?.example?.header_handle?.[0]) return null;
        const { name, extension } = getFileNameAndExtension(
          header.example?.header_handle?.[0],
        );
        return (
          <Box className={classes.document}>
            <ListItem
              className={classes.listItem}
              title="Baixar documento"
              component={Link}
              target="_blank"
              rel="noopener noreferrer"
              href={header.example?.header_handle?.[0]}
              disableGutters
            >
              <ListItemAvatar>
                <InsertDriveFileIcon fontSize="large" color="inherit" />
              </ListItemAvatar>
              <Typography variant="body2" className={classes.titulo}>
                {`${name}${extension ? `.${extension}` : ''}`}
              </Typography>
            </ListItem>
          </Box>
        );
      }
      case FormatosDeCabecalhoMensagemTemplateEnum.VIDEO: {
        const playerOptions = {
          url: header?.example?.header_handle?.[0],
          pip: true,
          controls: true,
          width: '100%',
          height: '100%',
          playIcon: (
            <IconButton className={classes.iconButton}>
              <PlayArrowIcon color="inherit" fontSize="large" />
            </IconButton>
          ),
        } as ReactPlayerProps;
        return <ReactPlayer {...playerOptions} />;
      }
      default:
        return null;
    }
  };

  // TEMPLATE PIX
  if (template && template.subcategory === 'ORDER_DETAILS') {
    return <TemplateMessagePix template={template} fromMe={fromMe} />;
  }

  return (
    <Box className={classes.content}>
      <Box className={classes.messageContainer}>
        {header && header.format && <Box>{renderHeader(header.format)}</Box>}

        {body && (
          <Typography
            className={classes.bodyText}
            dangerouslySetInnerHTML={{
              __html: parseTextToHtml(body.text || '', searchQuery),
            }}
          />
        )}

        {footer && (
          <Typography
            className={classes.footerText}
            dangerouslySetInnerHTML={{
              __html: parseTextToHtml(footer?.text || '', searchQuery),
            }}
          />
        )}

        <TemplateButtons
          buttons={buttons?.buttons}
          onShowMore={onShowMoreButtons}
        />

        {!hasContent && (
          <Typography className={`${classes.bodyText} ${classes.errorText}`}>
            Mensagem de template com formato não suportado
          </Typography>
        )}
      </Box>
    </Box>
  );
}
